import { Box, Button, Flex, Input, Stack } from '@chakra-ui/react';
import { Plus, Trash } from '@phosphor-icons/react';

import { useCreateJob } from '@/store/createJob';
import { Stepper } from '@/types/enums';
import RichEditor from '@/components/forms/RichEditor';

export default function AIAnalysis() {
  const createJobStore = useCreateJob();

  const navigateToStep = (step: Stepper) => {
    createJobStore.setStep(step);
    createJobStore.setCurrentPage(step);
  };

  const handleBack = () => navigateToStep(Stepper.JobDetails);

  const handleNext = () => {
    if (!createJobStore.isStepEnabled(Stepper.Review)) {
      createJobStore.setStep(Stepper.Review);
    }
    navigateToStep(Stepper.Review);
  };

  return (
    <>
      <Box
        w="100%"
        border="2px solid"
        borderRadius="12px"
        borderColor="neutrals.300"
        bg="background.secondary"
      >
        <Box display="flex" flexDirection="column" p="32px">
          <Box fontWeight="bold" fontSize="36px" mb="8px">
            AI Analysis
          </Box>
          <Box color="neutrals.500" mb="40px">
            Compares the uploaded job description with the company profile and job market data.
          </Box>
          <Box fontWeight="bold" fontSize="24px" mb="8px">
            Job Description
          </Box>
          <Box mb="32px">
            <RichEditor
              key="job-description"
              content={createJobStore.jobDescription}
              type="job-description"
              readOnly
              className="read-only"
            />
          </Box>
          <Box fontWeight="bold" fontSize="18px" mb="8px">
            Our AI analyzes the uploaded job description by comparing it with your company profile
            and industry benchmarks.
          </Box>
          <Box fontWeight="regular" fontSize="16px" color="neutrals.700" mb="20px">
            You can edit, remove, or add new requirements to align with your hiring needs.
          </Box>
          {['Requirements', 'Nice To Have'].map((section, sectionIndex) => (
            <Box
              key={sectionIndex}
              borderRadius="12px"
              border="1px solid"
              borderColor="neutrals.300"
              width="80%"
              mb="20px"
            >
              <Stack direction="column">
                <Flex
                  justifyContent="space-between"
                  alignItems="center"
                  bg="neutrals.50"
                  p="16px 32px"
                  borderBottom="1px solid"
                  borderColor="neutrals.300"
                  borderTopRadius="12px"
                >
                  <Box fontWeight="bold" fontSize="18px">
                    {section}
                  </Box>
                  <Button
                    size="sm"
                    bg="background.secondary"
                    borderColor="primary.900"
                    borderRadius="8px"
                    color="primary.900"
                    fontSize="14px"
                    onClick={() =>
                      section === 'Requirements'
                        ? createJobStore.addRequirement()
                        : createJobStore.addNiceToHave()
                    }
                  >
                    <Plus size={24} />
                    Add New
                  </Button>
                </Flex>
                {(section === 'Requirements'
                  ? createJobStore.requirements
                  : createJobStore.niceToHave
                ).map((item, index) => (
                  <Flex
                    key={index}
                    justifyContent="space-between"
                    alignItems="center"
                    p="10px 32px 16px 16px"
                    borderBottom={
                      index !==
                      (section === 'Requirements'
                        ? createJobStore.requirements.length
                        : createJobStore.niceToHave.length) -
                        1
                        ? '1px solid'
                        : 'none'
                    }
                    borderColor="neutrals.300"
                  >
                    <Input
                      w="80%"
                      ml={2}
                      value={item}
                      border="1px solid"
                      borderColor="neutrals.300"
                      borderRadius="8px"
                      bg="transparent"
                      fontSize="16px"
                      color="inherit"
                      onChange={(e) =>
                        section === 'Requirements'
                          ? createJobStore.updateRequirement(index, e.target.value)
                          : createJobStore.updateNiceToHave(index, e.target.value)
                      }
                    />
                    <Trash
                      size={20}
                      cursor="pointer"
                      onClick={() =>
                        section === 'Requirements'
                          ? createJobStore.removeRequirement(index)
                          : createJobStore.removeNiceToHave(index)
                      }
                    />
                  </Flex>
                ))}
              </Stack>
            </Box>
          ))}
        </Box>
      </Box>
      <Box h="64px" />
      <Flex justifyContent="flex-end">
        <Button
          w="120px"
          h="48px"
          borderRadius="8px"
          borderColor="primary.900"
          color="primary.900"
          bg="background.secondary"
          fontSize="16px"
          fontWeight="regular"
          mr="16px"
          onClick={handleBack}
        >
          Back
        </Button>
        <Button
          w="120px"
          h="48px"
          borderRadius="8px"
          bg="primary.900"
          fontSize="16px"
          fontWeight="regular"
          onClick={handleNext}
        >
          Next
        </Button>
      </Flex>
    </>
  );
}
